// Sample SFX library over the CC0 freesound harvest. Roles come from
// assets/sfx/sfx-index.json (built by tools/curate-sfx.mjs); buffers decode
// lazily per role on first use so boot stays light. Playback goes through
// GameAudio's context/master (user-gesture rule handled there).
//
// Many harvested takes are RECORDINGS OF SEQUENCES (a 10 s walk, several
// caws) — `slice` plays a short faded window at a random offset so one call
// sounds like one event instead of the whole take.

export class Sfx {
  constructor(audio) {
    this.audio = audio;
    this.index = null;
    this.buffers = new Map(); // role -> AudioBuffer[]
    this.loading = new Map(); // role -> Promise
    this.last = new Map();    // role -> last index (avoid immediate repeat)
  }

  async init() {
    try {
      this.index = (await (await fetch('assets/sfx/sfx-index.json')).json()).roles;
    } catch { this.index = null; }
    return this;
  }

  has(role) { return !!this.index?.[role]?.length; }

  _group(role) {
    if (this.buffers.has(role)) return this.buffers.get(role);
    if (this.loading.has(role)) return null; // decode in flight
    const files = this.index?.[role];
    const c = this.audio.ctx;
    if (!files || !c) return null;
    const p = Promise.all(files.map(async (f) => {
      try {
        const ab = await (await fetch(`assets/sfx/${f}`)).arrayBuffer();
        return await c.decodeAudioData(ab);
      } catch { return null; }
    })).then((bufs) => {
      this.buffers.set(role, bufs.filter(Boolean));
      this.loading.delete(role);
      return this.buffers.get(role);
    });
    this.loading.set(role, p);
    return null;
  }

  warm(...roles) { for (const r of roles) if (this.has(r) && this.audio.ctx) this._group(r); }

  // Fire-and-forget one-shot. Returns true if a sample actually started, so
  // callers can fall back to their procedural sound this once.
  // opts: vol, rate, jitter (± playbackRate spread), delay s, slice s (play a
  // faded window at a random offset when the take is longer), pan -1..1
  play(role, { vol = 0.5, rate = 1, jitter = 0.08, delay = 0, slice = 0, pan = 0 } = {}) {
    const c = this.audio.ctx;
    if (!c || c.state !== 'running' || !this.has(role)) return false;
    const bufs = this._group(role);
    if (!bufs || !bufs.length) return false;
    let i = Math.floor(Math.random() * bufs.length);
    if (bufs.length > 1 && i === this.last.get(role)) i = (i + 1) % bufs.length;
    this.last.set(role, i);
    const buf = bufs[i];
    const src = c.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate * (1 + (Math.random() * 2 - 1) * jitter);
    const g = c.createGain();
    src.connect(g);
    let tail = g;
    if (pan && c.createStereoPanner) {
      const p = c.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      g.connect(p); tail = p;
    }
    tail.connect(this.audio.master);
    const t = c.currentTime + delay;
    if (slice > 0 && buf.duration > slice + 0.2) {
      const off = Math.random() * (buf.duration - slice - 0.1);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.015);
      g.gain.setValueAtTime(vol, t + slice - 0.12);
      g.gain.linearRampToValueAtTime(0.0001, t + slice);
      src.start(t, off, slice + 0.05);
    } else {
      g.gain.value = vol;
      src.start(t);
    }
    return true;
  }

  // Looping ambience bed. Returns a handle synchronously (gain is drivable at
  // once); the source starts when the buffer lands. One random take per bed.
  bed(role) {
    const c = this.audio.ctx;
    if (!c || !this.has(role)) return null;
    const g = c.createGain();
    g.gain.value = 0;
    g.connect(this.audio.master);
    const h = { g, started: false };
    const kick = () => {
      const bufs = this._group(role);
      if (!bufs) { (this.loading.get(role) || Promise.resolve()).then(kick); return; }
      if (!bufs.length || h.started) return;
      h.started = true;
      const src = c.createBufferSource();
      src.buffer = bufs[Math.floor(Math.random() * bufs.length)];
      src.loop = true;
      // skip in a bit so multiple beds don't all start at their file's intro
      src.connect(g);
      src.start(0, Math.random() * Math.max(0, src.buffer.duration - 1));
    };
    kick();
    return h;
  }
}
